export type Point = [number, number];
export type Rgb = [number, number, number];

export interface PreprocessSettings {
  invert: boolean;
  brightness: number;
  contrast: number;
  gamma: number;
  blurSigma: number;
  sharpen: number;
  edgeEnhance: number;
  threshold: number | null;
  normalize: boolean;
  edgeWeight: number;
}

export type PinShape =
  | { type: 'circle' }
  | { type: 'rectangle' }
  | { type: 'polygon'; points: Point[] }
  | { type: 'custom'; points: Point[] };

export type PinDistribution = { type: 'uniform' } | { type: 'jittered'; amount: number };

export interface PinConfig {
  shape: PinShape;
  count: number;
  padding: number;
  rotation: number;
  scale: number;
  minDistance: number;
  distribution: PinDistribution;
}

export type ScoringKind = 'squaredError' | 'absoluteError';
export type LineNormalization = 'total' | 'perPixel';
export type StrategyKind = 'greedy';
export type Connectivity = 'continuous' | 'free';
export type LineAlgorithm = 'bresenham' | 'wu';

export interface AlgorithmConfig {
  maxStrings: number;
  minImprovement: number;
  stringAlpha: number;
  scoring: ScoringKind;
  normalization: LineNormalization;
  strategy: StrategyKind;
  connectivity: Connectivity;
  lineAlgorithm: LineAlgorithm;
  candidateSampleFraction: number;
  startPin: number;
  maxMaterializedCandidates: number;
  rasterCacheBudgetBytes: number;
  errorResyncInterval: number;
}

export interface LineConstraints {
  minLineLength: number;
  maxLineLength: number;
  minPinSeparation: number;
  maxLinesPerPin: number;
  allowRepeatedConnections: boolean;
  allowAdjacentPins: boolean;
  avoidPreviousLine: boolean;
  avoidRecentlyUsedPins: number;
}

export interface FrameMask {
  enabled: boolean;
  outsideWeight: number;
}

export interface EngineConfig {
  preprocess: PreprocessSettings;
  pins: PinConfig;
  algorithm: AlgorithmConfig;
  constraints: LineConstraints;
  frameMask: FrameMask;
  seed: number;
}

export interface RenderSettings {
  stringColor: Rgb;
  stringOpacity: number;
  stringThickness: number;
  backgroundColor: Rgb;
  showPins: boolean;
  pinColor: Rgb;
  pinRadius: number;
}

export type StopReason = 'maxStrings' | 'noImprovement' | 'deadEnd' | 'cancelled';
export type ConfigChange = 'none' | 'live' | 'reprepare' | 'regeneratePins' | 'reprocess';

export interface StepInfo {
  applied: boolean;
  index: number;
  from: number;
  to: number;
  score: number;
  gain: number;
  error: number;
  stop: StopReason | null;
}

export interface RunSummary {
  steps: number;
  totalStrings: number;
  error: number;
  stop: StopReason | null;
}

export interface CandidateStats {
  pinCount: number;
  pairCount: number;
  candidateCount: number;
  materialized: boolean;
  cached: boolean;
  cachedPixels: number;
  memoryBytes: number;
}

export interface PrepareStats {
  pinCount: number;
  candidates: CandidateStats;
  targetWidth: number;
  targetHeight: number;
  initialError: number;
}

export interface EngineStats {
  prepared: boolean;
  targetWidth: number;
  targetHeight: number;
  pinCount: number;
  stringCount: number;
  error: number;
  mae: number;
  mse: number;
  candidates: CandidateStats;
  memoryBytes: number;
  strategy: string;
  stopReason: StopReason | null;
  currentPin: number;
}

export interface PhysicalOptions {
  mmPerPixel: number;
  originMm: [number, number];
  flipY: boolean;
}

export interface PhysicalStep {
  sequence: number;
  from: number;
  to: number;
  lengthMm: number;
  travelMm: number;
}

export interface PhysicalPlan {
  pinPositionsMm: [number, number][];
  steps: PhysicalStep[];
  totalStringLengthMm: number;
  totalTravelMm: number;
  pinVisits: number[];
  isContinuous: boolean;
  threadBreaks: number;
}

export interface SvgOptions {
  width: number;
  height: number;
  includePins: boolean;
  includeBackground: boolean;
}

export interface GcodeOptions {
  feedRate: number;
  travelRate: number;
  safeZ: number;
  workZ: number;
}

export interface ExportOptions {
  svg: SvgOptions;
  physical: PhysicalOptions;
  gcode: GcodeOptions;
}

export type ExportFormat = 'svg' | 'csv' | 'json' | 'dxf' | 'gcode' | 'png';

export type SourceKind = 'image' | 'synthetic' | 'unknown';

export interface SourceMetadata {
  kind: SourceKind;
  name: string;
  width: number;
  height: number;
}

export const PROJECT_VERSION = 1 as const;

export interface Project {
  version: typeof PROJECT_VERSION;
  coreVersion: string;
  source: SourceMetadata;
  target: { width: number; height: number };
  config: EngineConfig;
  pinCoordinates: Point[];
  sequence: [number, number][];
  render: RenderSettings;
}

export function defaultPreprocess(): PreprocessSettings {
  return {
    invert: false,
    brightness: 0,
    contrast: 1,
    gamma: 1,
    blurSigma: 0,
    sharpen: 0,
    edgeEnhance: 0,
    threshold: null,
    normalize: false,
    edgeWeight: 0,
  };
}

export function defaultPins(): PinConfig {
  return {
    shape: { type: 'circle' },
    count: 240,
    padding: 4,
    rotation: 0,
    scale: 1,
    minDistance: 0,
    distribution: { type: 'uniform' },
  };
}

export function defaultAlgorithm(): AlgorithmConfig {
  return {
    maxStrings: 3000,
    minImprovement: 0,
    stringAlpha: 0.25,
    scoring: 'squaredError',
    normalization: 'perPixel',
    strategy: 'greedy',
    connectivity: 'continuous',
    lineAlgorithm: 'wu',
    candidateSampleFraction: 1,
    startPin: 0,
    maxMaterializedCandidates: 2_000_000,
    rasterCacheBudgetBytes: 256 * 1024 * 1024,
    errorResyncInterval: 256,
  };
}

export function defaultConstraints(): LineConstraints {
  return {
    minLineLength: 0,
    maxLineLength: 0,
    minPinSeparation: 0,
    maxLinesPerPin: 0,
    allowRepeatedConnections: false,
    allowAdjacentPins: false,
    avoidPreviousLine: true,
    avoidRecentlyUsedPins: 0,
  };
}

export function defaultEngineConfig(): EngineConfig {
  return {
    preprocess: defaultPreprocess(),
    pins: defaultPins(),
    algorithm: defaultAlgorithm(),
    constraints: defaultConstraints(),
    frameMask: { enabled: true, outsideWeight: 0 },
    seed: 0,
  };
}

export function defaultRenderSettings(): RenderSettings {
  return {
    stringColor: [20, 20, 24],
    stringOpacity: 0.25,
    stringThickness: 1,
    backgroundColor: [250, 249, 246],
    showPins: false,
    pinColor: [140, 140, 140],
    pinRadius: 2,
  };
}

export function defaultExportOptions(): ExportOptions {
  return {
    svg: { width: 0, height: 0, includePins: true, includeBackground: true },
    physical: { mmPerPixel: 1, originMm: [0, 0], flipY: true },
    gcode: { feedRate: 1500, travelRate: 3000, safeZ: 5, workZ: 0 },
  };
}

export function cloneConfig<T>(value: T): T {
  return structuredClone(value);
}
